import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionsContext';
import { useLanguage } from '../contexts/LanguageContext';
import {
  SquaresFour, CalendarBlank, Users, Flask,
  Buildings, Package, GearSix, ClipboardText,
  SignOut, List, CaretDown, Translate, Key, ShieldCheck, UserCircle, ChatCircleDots, UsersFour, Lifebuoy, ChartBar
} from '@phosphor-icons/react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import {
  DropdownMenu, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { Separator } from '../components/ui/separator';
import PinSetup from './PinSetup';
import SupportFloat from './support/SupportFloat';
import NotificationsBell from './NotificationsBell';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const navItems = [
  { key: 'dashboard', path: '/', icon: SquaresFour },
  { key: 'agenda', path: '/agenda', icon: CalendarBlank },
  { key: 'recruitment', path: '/recruitment', icon: UsersFour },
  { key: 'volunteers', path: '/volunteers', icon: Users },
  { key: 'studies', path: '/studies', icon: Flask },
  { key: 'clients', path: '/clients', icon: Buildings },
  { key: 'communications', path: '/communications', icon: ChatCircleDots },
  { key: 'warehouse', path: '/warehouse', icon: Package },
  { key: 'statistics', path: '/statistics', icon: ChartBar },
  { key: 'settings', path: '/settings', icon: GearSix },
  { key: 'audit', path: '/audit', icon: ClipboardText },
  { key: 'support', path: '/support', icon: Lifebuoy, requiresEdit: true },
];

const roleLabels = {
  admin: 'Administrador',
  investigador_principal: 'Inv. Principal',
  investigador: 'Co-Investigador',
  director_estudio: 'Director Estudio',
  monitor: 'Monitor',
  coordinador: 'Coordinador',
  data_manager: 'Data Manager',
  sondas: 'Sondas',
  reclutamiento: 'Reclutamiento',
};

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { t, lang, toggleLanguage } = useLanguage();
  const { can, perms } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();
  // URS-F505: si la pagina esta montada dentro de un iframe embebido
  // (flujo Agenda > "Revisar Qsalud"), arrancamos con el sidebar
  // colapsado para maximizar el area util del Qsalud dentro del popup.
  const _initialSidebarOpen = typeof window !== 'undefined'
    && new URLSearchParams(window.location.search).get('embedded') === '1'
    ? false : true;
  const [sidebarOpen, setSidebarOpen] = useState(_initialSidebarOpen);
  const [showPinSetup, setShowPinSetup] = useState(false);
  const [hasPin, setHasPin] = useState(false);
  // URS-F195: contador de tickets abiertos + en_curso al lado del item Soporte
  const [supportPendingCount, setSupportPendingCount] = useState(0);

  // Filter nav items by permissions (hide entire module if no view).
  // While perms are loading we render the full menu to avoid flicker; users without
  // view will get a redirect when they try to access a route.
  // URS-F194: support sidebar entry is admin-only (requires `edit`); regular users
  // access the support center via the floating widget.
  // URS-F321 (decision 2a): los directores puros (sin otros roles operacionales)
  // no deben tener acceso al modulo global Voluntarios (solo a voluntarios
  // dentro de cada estudio asignado, donde ya se enmascara la PII).
  const userRoles = user?.roles || [];
  const _operationalRoles = new Set(["coordinador", "investigador_principal", "monitor",
    "data_manager", "supervisor_sondas", "sondas", "reclutamiento",
    "supervisor_reclutamiento", "tecnico", "auxiliar"]);
  const isPureDirector = !userRoles.includes("admin") && userRoles.includes("director_estudio") &&
    !userRoles.some(r => _operationalRoles.has(r));

  const visibleNavItems = perms ? navItems.filter(item => {
    // URS-F321: ocultar entrada Voluntarios a directores puros.
    if (item.key === "volunteers" && isPureDirector) return false;
    if (item.requiresEdit) return can(item.key, 'edit');
    return can(item.key, 'view');
  }) : navItems.filter(item => !item.requiresEdit && !(item.key === "volunteers" && isPureDirector));

  useEffect(() => {
    if (user) {
      axios.get(`${API_URL}/api/auth/pin-status`, { withCredentials: true })
        .then(({ data }) => setHasPin(data.has_pin))
        .catch(() => {});
    }
  }, [user]);

  // URS-F195: refresca cada 60s el contador de tickets abiertos+en_curso si el
  // usuario tiene acceso al modulo de soporte como admin/soporte. Tambien se
  // refresca al cambiar de ruta para captar tickets creados desde el flotante.
  useEffect(() => {
    if (!user || !can('support', 'edit')) {
      setSupportPendingCount(0);
      return undefined;
    }
    let cancelled = false;
    const fetchPending = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/support/tickets/stats`, { withCredentials: true });
        if (!cancelled) {
          const pending = (data?.by_status?.abierto || 0) + (data?.by_status?.en_curso || 0);
          setSupportPendingCount(pending);
        }
      } catch {
        if (!cancelled) setSupportPendingCount(0);
      }
    };
    fetchPending();
    const iv = setInterval(fetchPending, 60000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [user, can, location.pathname]);

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const currentPage = location.pathname === '/profile' ? 'profile' : (navItems.find(i => isActive(i.path))?.key || 'dashboard');

  return (
    <div className="flex h-screen bg-[#F9F8F6]" data-testid="app-layout">
      {/* Sidebar */}
      <aside
        className={`${sidebarOpen ? 'w-64' : 'w-0'} transition-all duration-150 ease-in-out bg-white border-r border-[#E5E4E0] flex flex-col overflow-hidden shrink-0`}
        data-testid="sidebar"
      >
        {/* Brand */}
        <div className="h-16 flex items-center justify-center border-b border-[#E5E4E0] shrink-0">
          <img src="/imagotipo.png" alt="Goya Innova" className="h-11" />
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-3 px-3 overflow-y-auto">
          <div className="animate-stagger">
            {visibleNavItems.map(item => {
              const Icon = item.icon;
              const active = isActive(item.path);
              return (
                <button
                  key={item.key}
                  data-testid={`sidebar-nav-${item.key}`}
                  onClick={() => navigate(item.path)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-all duration-150 mb-0.5
                    ${active
                      ? 'bg-stone-100 text-[#1f3e80] font-medium border-l-[3px] border-[#1f3e80] pl-[9px]'
                      : 'text-stone-600 hover:bg-stone-50 hover:text-stone-900 border-l-[3px] border-transparent pl-[9px]'
                    }`}
                >
                  <Icon size={20} weight={active ? 'duotone' : 'regular'} />
                  <span className="font-body whitespace-nowrap">{t(item.key)}</span>
                  {item.key === 'support' && supportPendingCount > 0 && (
                    <span
                      data-testid="sidebar-support-badge"
                      className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-semibold bg-[#1f3e80] text-white"
                    >
                      {supportPendingCount > 99 ? '99+' : supportPendingCount}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </nav>

        <Separator />

        {/* User at bottom */}
        <div className="p-4 shrink-0">
          <button
            onClick={() => navigate('/profile')}
            className="flex items-center gap-3 w-full text-left hover:bg-stone-100 rounded-sm p-1.5 -m-1.5 transition-colors"
            data-testid="sidebar-profile-btn"
          >
            <Avatar className="h-8 w-8 shrink-0">
              <AvatarFallback className="bg-[#1f3e80] text-white text-xs font-medium">
                {user?.name?.charAt(0)?.toUpperCase() || 'U'}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-stone-900 truncate font-body">{user?.name}</p>
              <p className="text-xs text-stone-500 truncate font-body">{(user?.roles || [user?.role]).filter(Boolean).map(r => roleLabels[r] || r).join(', ')}</p>
            </div>
          </button>
        </div>
      </aside>

      {/* Main area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="h-16 sticky top-0 z-10 bg-[#F9F8F6]/95 backdrop-blur-sm border-b border-[#E5E4E0] flex items-center justify-between px-6 shrink-0">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              data-testid="toggle-sidebar-btn"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="h-8 w-8 text-stone-500 hover:text-stone-900 hover:bg-stone-100"
            >
              <List size={18} />
            </Button>
            <div className="flex items-center gap-2">
              <span className="text-xs font-mono text-stone-400 uppercase tracking-wider">eCRD</span>
              <span className="text-stone-300">/</span>
              <span className="text-sm font-medium text-stone-700 font-body">{t(currentPage)}</span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Language toggle */}
            <Button
              variant="ghost"
              size="sm"
              data-testid="language-toggle-btn"
              onClick={toggleLanguage}
              className="h-8 px-3 text-xs font-mono text-stone-500 hover:text-stone-900 hover:bg-stone-100 gap-1.5"
            >
              <Translate size={14} />
              {lang === 'es' ? 'EN' : 'ES'}
            </Button>

            <Separator orientation="vertical" className="h-6" />

            {/* Avisos (notificaciones in-app) */}
            <NotificationsBell lang={lang} />

            {/* User menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  data-testid="user-menu-btn"
                  className="h-8 px-2 flex items-center gap-2 text-stone-600 hover:text-stone-900 hover:bg-stone-100"
                >
                  <Avatar className="h-6 w-6">
                    <AvatarFallback className="bg-[#1f3e80] text-white text-[10px] font-medium">
                      {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-sm font-body hidden sm:inline">{user?.name}</span>
                  <CaretDown size={12} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium text-stone-900">{user?.name}</p>
                  <p className="text-xs text-stone-500">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  data-testid="profile-menu-btn"
                  onClick={() => navigate('/profile')}
                  className="cursor-pointer flex items-center gap-2"
                >
                  <UserCircle size={16} className="text-[#1f3e80]" />
                  <span>{lang === 'es' ? 'Mi perfil' : 'My profile'}</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="pin-setup-menu-btn"
                  onClick={() => setShowPinSetup(true)}
                  className="cursor-pointer flex items-center gap-2"
                >
                  <Key size={16} className="text-[#1f3e80]" />
                  <span>{lang === 'es' ? 'PIN de firma' : 'Signature PIN'}</span>
                  {hasPin
                    ? <ShieldCheck size={14} className="ml-auto text-green-600" />
                    : <span className="ml-auto text-[10px] text-amber-600 font-medium">{lang === 'es' ? 'Sin PIN' : 'No PIN'}</span>
                  }
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  data-testid="logout-btn"
                  onClick={logout}
                  className="text-red-600 focus:text-red-600 cursor-pointer"
                >
                  <SignOut size={16} className="mr-2" />
                  {t('logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-auto p-6 sm:p-8">
          {children}
        </main>
      </div>

      {/* PIN Setup Dialog */}
      <PinSetup
        open={showPinSetup}
        onClose={() => setShowPinSetup(false)}
        lang={lang}
        onPinSet={() => setHasPin(true)}
      />

      {/* URS-F194: floating support widget (visible to any authed internal user) */}
      <SupportFloat lang={lang} />
    </div>
  );
}
